import { Component, forwardRef, inject, input } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { BuildingAllocationDataDto, ManagerDetails, TenantItemDto, UserItemDto } from '@artemis-software/wr-api';
import { AbstractFormControl } from '@shared/form-controls/abstract-form-control';
import { createForm } from '@sonofabit/ng-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs';
import { NbDialogService } from '@nebular/theme';
import {
  PropertyManagerDialogComponent
} from '@components/dialog/property-manager-dialog/property-manager-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-building-allocation-form',
  templateUrl: './building-allocation-form.component.html',
  styleUrl: './building-allocation-form.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuildingAllocationFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => BuildingAllocationFormComponent),
      multi: true,
    }
  ],
})
export class BuildingAllocationFormComponent extends AbstractFormControl<BuildingAllocationDataDto> implements Validator {
  private readonly dialogService = inject(NbDialogService);

  readonly showTeamRegion = input(true);

  protected readonly TenantTypeEnum = TenantItemDto.TypeEnum;

  readonly form = createForm(({ field }) => {
    return {
      ownerTenant: field({ value: null as TenantItemDto | null }),
      team: field({ value: null as string | null }),
      region: field({ value: null as string | null }),
      propertyManager: field({ value: null as UserItemDto | null }),
      embeddedPropertyManager: field({ value: null as ManagerDetails | null }),
      owner: field({ value: BuildingAllocationDataDto.OwnerEnum.No }),
      propertyCaretaker: field({ value: '' }),
      assistant: field({ value: '' }),
      ownerTenantId: field({ value: '', validators: [Validators.required] }),
    };
  });

  override setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }
  }

  constructor() {
    super();
    this.form.valueChanges.pipe(takeUntilDestroyed()).subscribe(val => {
      this.value = val as unknown as BuildingAllocationDataDto;
      this.onValidatorChange?.();
    });
  }

  override writeValue(value: BuildingAllocationDataDto | undefined) {
    super.writeValue(value);
    if (value) {
      this.form.patchValue({
        ...value,
        ownerTenantId: value.ownerTenant?.id ?? '',
        team: value.team?.name ?? '',
        region: value.team?.region?.name ?? '',
      }, { emitEvent: false });
    } else {
      this.form.reset({ owner: BuildingAllocationDataDto.OwnerEnum.No }, { emitEvent: false });
    }
  }

  override markAsTouched() {
    super.markAsTouched();
    this.form.markAllAsTouched();
  }

  getPropertyManagerName(): string {
    const propertyManager = this.form.get('propertyManager')?.value;
    const embeddedPropertyManager = this.form.get('embeddedPropertyManager')?.value;

    if (propertyManager) return `${propertyManager.firstName} ${propertyManager.lastName}`;
    if (embeddedPropertyManager) return `${embeddedPropertyManager.propertyManagerFirstName} ${embeddedPropertyManager.propertyManagerLastName}`;
    return '';
  }

  openPropertyManagerDialog() {
    this.dialogService.open(PropertyManagerDialogComponent, {
      context: {
        buildingTenantId: this.form.get('ownerTenantId')?.value,
        propertyManager: this.form.get('propertyManager')?.value || this.form.get('embeddedPropertyManager')?.value,
      },
    }).onClose.pipe(take(1)).subscribe((result: { propertyManager?: UserItemDto, embeddedPropertyManager?: ManagerDetails } | undefined) => {
      if (!result) return;

      if (result.propertyManager) {
        this.form.patchValue({
          propertyManager: result.propertyManager,
          embeddedPropertyManager: null
        });
      } else if (result.embeddedPropertyManager) {
        this.form.patchValue({
          embeddedPropertyManager: result.embeddedPropertyManager,
          propertyManager: null
        });
      }
    });
  }

  hasError(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!(control?.invalid && (control.dirty || control.touched));
  }

  validate(): ValidationErrors | null {
    return this.form.invalid ? { allocationDataInvalid: true } : null;
  }

  private onValidatorChange?: () => void;
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }
}
